/** Guards are UX-only; see ../components/Authentication/README.md (DEV-4482). */
import React from "react";
import { Navigate } from "react-router-dom";

import { RequireRole } from "@cyngn/auth-react";

import { useHasAdminAccess } from "Components/Authentication/useRoleAccess";
import { CanonicalRole } from "Utils/rbac";
import { ChildrenProps } from "Utils/types";

export const OnlyForAdminGuard = ({ children }: ChildrenProps) => {
  const hasAdminAccess = useHasAdminAccess();

  if (!hasAdminAccess) {
    return <Navigate to="/" />;
  }

  return <>{children}</>;
};

interface RoleGuardProps extends ChildrenProps {
  /** Any one of these canonical roles grants the route. */
  roles: CanonicalRole[];
  redirectTo?: string;
}

/**
 * Guard for routes that exist only in the RBAC world (`/admin/*`), built on the shared
 * library's `RequireRole` (DEV-5751). A session with no canonical role is redirected like any
 * other unauthorized role. `autoLogin` is off because the app decides where an unauthorized
 * session goes (`redirectTo`), and a session still bootstrapping renders nothing rather than
 * being redirected on a role the provider has not read yet.
 */
export const RoleGuard = ({
  roles,
  redirectTo = "/",
  children,
}: RoleGuardProps) => {
  const redirect = <Navigate to={redirectTo} replace />;

  return (
    <RequireRole
      roles={roles}
      autoLogin={false}
      fallback={redirect}
      forbidden={redirect}
    >
      {children}
    </RequireRole>
  );
};
